import { Divider } from 'primereact/divider';
import { useMemo, FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InvoiceDetail.module.scss';
import { Value, parseInvoiceData } from './helpers';

export type ItemProps = {
  label: string;
  value: number | null;
  items?: ItemProps[];
};

type InvoiceItemListProps = {
  items: ItemProps[];
  total: number | null;
};

const InvoiceItem: FC<ItemProps> = ({ label, value, items }) => (
  <>
    <div className={styles.items}>
      <span className={styles.label}>{label}</span>
      <span className={styles.price}>
        {value?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </div>
    {items?.map((item) => (
      <div key={`${item.label}-${item.value}`} className={styles.subitem}>
        <span>{item.label}</span>
        <span>
          {item.value
            ? item.value?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : null}
        </span>
      </div>
    ))}
    <Divider className={`mb-0 ${styles.customDivider}`} />
  </>
);

const TotalInvoiceItem: FC<{ label: string; value: number | null }> = ({
  label,
  value,
}) => (
  <div className={styles.itemsTotal}>
    <span className={styles.labelTotal}>{label}</span>
    <span>
      <span className={styles.labelValueIcon}>U$S</span>
      <span className={styles.labelValue}>
        {value?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </span>
  </div>
);

const InvoiceItemList: FC<InvoiceItemListProps> = ({ items, total }) => {
  const { t } = useTranslation('paymentInquiry');

  return (
    <div className={styles.invoiceItems}>
      {items?.map((item) => (
        <InvoiceItem
          key={`${item.label}-${item.value}`}
          label={item.label}
          value={item.value}
          items={item.items}
        />
      ))}
      {items && <TotalInvoiceItem label={t('total')} value={total} />}
    </div>
  );
};

const InvoiceDetail: FC<{ data: unknown }> = ({ data }) => {
  const body = useMemo(() => (data || {}) as Value, [data]);

  const { items, total } = useMemo(() => {
    return (
      parseInvoiceData({ data: body }) || {
        items: [],
        total: 0,
      }
    );
  }, [body]);

  return <InvoiceItemList items={items} total={total} />;
};

export default InvoiceDetail;
